import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useStore } from './lib/store'
import { Login } from './screens/auth/Login'
import { Register } from './screens/auth/Register'
import { AcceptInvite } from './screens/auth/AcceptInvite'
import { SelfVerify } from './screens/auth/SelfVerify'
import { Shell } from './layout/Shell'
import { Candidates } from './screens/Candidates'
import { CandidateProfile } from './screens/CandidateProfile'
import { SendVerification } from './screens/SendVerification'
import { Activity } from './screens/Activity'
import { Team } from './screens/Team'
import { Billing } from './screens/Billing'
import { Settings } from './screens/Settings'
import { AdminDashboard } from './screens/admin/Dashboard'
import { AdminSubscribers } from './screens/admin/Subscribers'
import { AdminAnnouncements } from './screens/admin/Announcements'
import { canAccessAdmin } from './lib/permissions'

function RequireAuth({ children }: { children: ReactNode }) {
  const { session } = useStore()
  const location = useLocation()
  if (!session.authed) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <>{children}</>
}

// Platform-staff gate for the /admin area. Assumes RequireAuth already ran (it
// wraps the same Shell), so a signed-in non-staff recruiter is bounced to their
// pipeline rather than the login page. The api sidecar re-checks isStaff.
function RequireStaff({ children }: { children: ReactNode }) {
  const { session } = useStore()
  if (!canAccessAdmin(session.recruiter)) {
    return <Navigate to="/candidates" replace />
  }
  return <>{children}</>
}

export function App() {
  const { session } = useStore()
  return (
    <Routes>
      {/* Auth */}
      <Route
        path="/login"
        element={session.authed ? <Navigate to="/candidates" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={session.authed ? <Navigate to="/candidates" replace /> : <Register />}
      />
      {/* Public — landing page for an emailed team-invite link. */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      {/* Public — the candidate's page for an emailed self-verify link. */}
      <Route path="/self-verify" element={<SelfVerify />} />

      {/* Console (shell-wrapped, auth-guarded) */}
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route path="/candidates" element={<Candidates />} />
        <Route path="/candidates/:id" element={<CandidateProfile />} />
        <Route path="/verify" element={<SendVerification />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/team" element={<Team />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/settings" element={<Settings />} />

        {/* Platform-staff admin area (cross-account; gated by isStaff) */}
        <Route path="/admin" element={<RequireStaff><AdminDashboard /></RequireStaff>} />
        <Route path="/admin/subscribers" element={<RequireStaff><AdminSubscribers /></RequireStaff>} />
        <Route path="/admin/announcements" element={<RequireStaff><AdminAnnouncements /></RequireStaff>} />
      </Route>

      <Route path="/" element={<Navigate to={session.authed ? '/candidates' : '/login'} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
